import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, TriangleAlert } from 'lucide-react';
import { ALLERGENS, MEAL_SLOT_LABELS, MEAL_SLOT_ORDER, type MealSlot } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, ErrorNote, Input, Spinner } from '@/components/ui';
import { api, apiData } from '@/lib/api';
import { addDays, mondayOf, todayISO } from '@/lib/format';
import type { MealWeek } from '@/types';

const WEEKDAY_LABELS = ['월', '화', '수', '목', '금'];

type Draft = Record<string, { menu: string; allergens: string[] }>;

const cellKey = (date: string, slot: MealSlot) => `${date}|${slot}`;

/** 식단 작성 — 명세서 §5.7 (주 단위로 통째로 저장) */
export function MealEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [weekStart, setWeekStart] = useState(() => mondayOf(todayISO()));
  const [draft, setDraft] = useState<Draft>({});
  const [openCell, setOpenCell] = useState<string | null>(null);

  const { data, isPending, isPlaceholderData } = useQuery({
    queryKey: ['meals', weekStart],
    queryFn: () => apiData<MealWeek>(`/meals?weekStart=${weekStart}`),
    placeholderData: keepPreviousData,
  });

  // 주를 바꾸면 그 주에 이미 저장된 식단을 불러와 편집 시작점으로 삼는다.
  // placeholder(이전 주) 데이터로는 draft를 덮지 않는다.
  useEffect(() => {
    if (!data || isPlaceholderData) return;
    const next: Draft = {};
    for (const item of data.items) {
      next[cellKey(item.mealDate, item.slot)] = {
        menu: item.menu.join(', '),
        allergens: item.allergens,
      };
    }
    setDraft(next);
  }, [data, isPlaceholderData]);

  const save = useMutation({
    mutationFn: () =>
      api('/meals', {
        method: 'POST',
        body: {
          weekStart,
          items: Object.entries(draft)
            .filter(([, value]) => value.menu.trim().length > 0)
            .map(([key, value]) => {
              const [mealDate, slot] = key.split('|');
              return {
                mealDate,
                slot,
                menu: value.menu
                  .split(',')
                  .map((entry) => entry.trim())
                  .filter(Boolean),
                allergens: value.allergens,
              };
            }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meals'] });
      navigate('/meals');
    },
  });

  const days = WEEKDAY_LABELS.map((_, index) => addDays(weekStart, index));

  const setCell = (key: string, patch: Partial<{ menu: string; allergens: string[] }>) =>
    setDraft((prev) => {
      const current = prev[key] ?? { menu: '', allergens: [] };
      return { ...prev, [key]: { ...current, ...patch } };
    });

  return (
    <Screen withNav={false}>
      <AppBar title="식단 작성" back />

      <div className="flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={() => setWeekStart((prev) => addDays(prev, -7))}
          aria-label="지난주"
          className="tap p-2"
        >
          <ChevronLeft size={20} />
        </button>
        <p className="text-base font-bold">
          {weekStart.slice(5).replace('-', '.')} 주
        </p>
        <button
          type="button"
          onClick={() => setWeekStart((prev) => addDays(prev, 7))}
          aria-label="다음주"
          className="tap p-2"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {isPending && <Spinner />}

      <div className="flex flex-col gap-3 p-4">
        {days.map((date, index) => (
          <Card key={date}>
            <p className="mb-2 font-semibold">
              {WEEKDAY_LABELS[index]}요일{' '}
              <span className="text-sm font-normal text-ink-sub">{date.slice(5)}</span>
            </p>

            <div className="flex flex-col gap-2">
              {MEAL_SLOT_ORDER.map((slot) => {
                const key = cellKey(date, slot);
                const cell = draft[key] ?? { menu: '', allergens: [] };
                return (
                  <div key={slot}>
                    <div className="flex items-center gap-2">
                      <span className="w-16 shrink-0 text-sm text-ink-sub">
                        {MEAL_SLOT_LABELS[slot]}
                      </span>
                      <Input
                        value={cell.menu}
                        placeholder="쉼표로 구분"
                        onChange={(e) => setCell(key, { menu: e.target.value })}
                      />
                      {/* 좁은 화면에서 줄바꿈되지 않도록 아이콘+개수로 줄인다 */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 px-2"
                        aria-label={`${MEAL_SLOT_LABELS[slot]} 알레르기 선택`}
                        onClick={() => setOpenCell(openCell === key ? null : key)}
                      >
                        <TriangleAlert size={16} />
                        {cell.allergens.length > 0 && (
                          <span className="tnum text-sm">{cell.allergens.length}</span>
                        )}
                      </Button>
                    </div>

                    {openCell === key && (
                      <div className="mt-2 flex flex-wrap gap-1.5 pl-16">
                        {ALLERGENS.map((allergen) => (
                          <Chip
                            key={allergen}
                            tone={cell.allergens.includes(allergen) ? 'secondary' : 'neutral'}
                            selected={cell.allergens.includes(allergen)}
                            onClick={() =>
                              setCell(key, {
                                allergens: cell.allergens.includes(allergen)
                                  ? cell.allergens.filter((a) => a !== allergen)
                                  : [...cell.allergens, allergen],
                              })
                            }
                          >
                            {allergen}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}

        {save.isError && <ErrorNote message={(save.error as Error).message} />}

        <p className="px-1 text-sm text-ink-sub">
          저장하면 이 주의 식단이 통째로 교체돼요. 알레르기는 원아 정보와 자동으로 대조돼요.
        </p>

        <Button block loading={save.isPending} onClick={() => save.mutate()}>
          저장
        </Button>
      </div>
    </Screen>
  );
}
